var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { SortDirection } from 'lib-shared-legacy-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSetState } from 'react-use';
const DEFAULT_LIMIT = 25;
/**
 * A hook that returns a list of items. It supports pagination & searching.
 *
 * @param endpoint The api call to get the items
 * @param selectItems A function to select the items from the response
 * @param selectTotal A function to select the total number of items from the
 *   response
 * @param infinite Whether the new results should be appended/prepended to the
 *   list, for infinite lists
 * @param initialParams Initial parameters for searching, filtering
 */
export const usePaginatedEndpoint = ({ endpoint, itemsSelector, totalSelector, infinite = false, resetSortOnSearch = false, initialParams, }) => {
    var _a, _b, _c, _d;
    // Data
    const [items, setItems] = useState([]);
    const [asyncStatus, setAsyncStatus] = useState('succeeded');
    // Pagination
    const [total, setTotal] = useState();
    const [nextPagePath, setNextPagePath] = useState(null);
    const [previousPagePath, setPreviousPagePath] = useState(null);
    // Filtering
    const [params, setParams] = useSetState(Object.assign(Object.assign({}, initialParams), { limit: (_a = initialParams.limit) !== null && _a !== void 0 ? _a : DEFAULT_LIMIT, search: (_b = initialParams.search) !== null && _b !== void 0 ? _b : '', sortKey: (_c = initialParams.sortKey) !== null && _c !== void 0 ? _c : null, sortDirection: (_d = initialParams === null || initialParams === void 0 ? void 0 : initialParams.sortDirection) !== null && _d !== void 0 ? _d : SortDirection.Ascending }));
    const getItems = useCallback(({ path, mergeStrategy = 'replace' } = {}) => __awaiter(void 0, void 0, void 0, function* () {
        setAsyncStatus('loading');
        try {
            const { sortKey, sortDirection } = params, rest = __rest(params, ["sortKey", "sortDirection"]);
            const sort_by = sortKey === null || (resetSortOnSearch && rest.search)
                ? ''
                : `${sortKey}:${sortDirection}`;
            const { data, meta } = yield (path
                ? endpoint({ path })
                : endpoint({
                    params: Object.assign(Object.assign({}, rest), { sort_by }),
                }));
            let newList = itemsSelector(data);
            if (mergeStrategy === 'append') {
                newList = [...items, ...newList];
            }
            else if (mergeStrategy === 'prepend') {
                newList = [...newList, ...items];
            }
            setItems(newList);
            if (totalSelector) {
                setTotal(totalSelector(data));
            }
            setAsyncStatus('succeeded');
            setNextPagePath(meta.next_page);
            setPreviousPagePath(meta.prev_page);
        }
        catch (error) {
            setAsyncStatus('failed');
        }
    }), [params, resetSortOnSearch, endpoint, itemsSelector, totalSelector, items]);
    const timeoutRef = useRef();
    const skipFetchRef = useRef(false);
    const debounceTimeRef = useRef(0);
    useEffect(() => {
        if (timeoutRef.current)
            clearTimeout(timeoutRef.current);
        if (skipFetchRef.current)
            return;
        // When params are changed, the list needs to be replaced with the new results, hence mergeStrategy = "replace"
        const updateList = () => getItems({ mergeStrategy: 'replace' });
        if (debounceTimeRef.current > 0) {
            timeoutRef.current = window.setTimeout(updateList, debounceTimeRef.current);
        }
        else {
            updateList();
        }
        // Excluding getItems from the dependencies arrary to prevent an infinite loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params]);
    const changeParams = useCallback((newParams, { skipFetch = false, debounceTime = 0 } = {}) => {
        skipFetchRef.current = skipFetch;
        debounceTimeRef.current = debounceTime;
        setParams((prevParams) => {
            const newState = Object.assign(Object.assign({}, prevParams), newParams);
            if (resetSortOnSearch && newState.search)
                newState.sortKey = null;
            return newState;
        });
    }, [resetSortOnSearch, setParams]);
    const nextPage = useCallback(() => {
        if (!nextPagePath) {
            throw Error('Next page does not exist');
        }
        return getItems({
            path: nextPagePath,
            mergeStrategy: infinite ? 'append' : 'replace',
        });
    }, [getItems, infinite, nextPagePath]);
    const previousPage = useCallback(() => {
        if (!previousPagePath) {
            throw Error('Previous page does not exist');
        }
        return getItems({
            path: previousPagePath,
            mergeStrategy: infinite ? 'prepend' : 'replace',
        });
    }, [getItems, infinite, previousPagePath]);
    const updateItem = useCallback((itemToUpdate, compareFn) => {
        if (!items.includes(itemToUpdate)) {
            setItems(items.map((item) => compareFn(item, itemToUpdate) ? itemToUpdate : item));
        }
    }, [items]);
    const refreshItems = useCallback(() => {
        return getItems({
            mergeStrategy: 'replace',
        });
    }, [getItems]);
    return {
        items,
        total,
        asyncStatus,
        params,
        hasNextPage: nextPagePath != null,
        hasPreviousPage: previousPagePath != null,
        nextPage,
        previousPage,
        changeParams,
        updateItem,
        refreshItems,
    };
};
